import * as React from 'react'

/**
 * The Top 10 category filter's selection.
 *
 * It is mirrored into the query string so a filtered board can be linked to and survives a reload,
 * which makes it an external store — what `useSyncExternalStore` reads. The server snapshot is the
 * default option, the board the prerendered HTML already shows, so the first client render matches
 * the markup it hydrates and React re-reads the URL right after.
 *
 * The URL is a mirror and not the record, because `replaceState` throws in WebKit once it has seen
 * ~100 calls in 30 seconds — which a held arrow key on the native select reaches in a couple of
 * seconds. A control whose only state was the URL would freeze there, so the selection is kept
 * here and written out best-effort.
 */

/** Value of the default option, which selects the unfiltered board. Not a category slug. */
export const ALL_CATEGORIES = 'all'

const CATEGORY_PARAM = 'cat'

/** The category a query string selects, or the default when it names none. */
export function categoryFromSearch(search: string): string {
  return new URLSearchParams(search).get(CATEGORY_PARAM) ?? ALL_CATEGORIES
}

/**
 * `search` with the category applied, leaving every other parameter alone — campaign tags reach
 * this page and must survive a filter change. The default option carries no parameter at all.
 */
export function searchWithCategory(search: string, slug: string): string {
  const params = new URLSearchParams(search)

  if (slug === ALL_CATEGORIES) {
    params.delete(CATEGORY_PARAM)
  } else {
    params.set(CATEGORY_PARAM, slug)
  }

  const query = params.toString()
  return query === '' ? '' : `?${query}`
}

/**
 * Set once the reader picks a category; until then the URL the page was opened with answers.
 *
 * Module scope rather than component state so `getSnapshot` can read it without a closure. The
 * filter is mounted once for the life of the page, so there is no second instance to confuse.
 */
let selectedCategory: string | null = null

const listeners = new Set<() => void>()

function subscribe(onChange: () => void): () => void {
  listeners.add(onChange)

  return () => {
    listeners.delete(onChange)
  }
}

function readCategory(): string {
  return selectedCategory ?? categoryFromSearch(window.location.search)
}

function selectCategory(slug: string): void {
  selectedCategory = slug

  try {
    // The hash is carried over: "voir le top 10" links to #top10, and dropping it here would
    // rewrite a URL the reader arrived on.
    const search = searchWithCategory(window.location.search, slug)
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${search}${window.location.hash}`)
  } catch {
    // Throttled by the browser. The selection still stands; only its shareable mirror is stale.
  }

  listeners.forEach((notify) => notify())
}

/** The selected category, and the setter that records it. */
export function useCategoryFilter(): [string, (slug: string) => void] {
  return [React.useSyncExternalStore(subscribe, readCategory, () => ALL_CATEGORIES), selectCategory]
}
